import { z } from 'zod';
import { pageSchema, timestampSchema } from './common';

/**
 * Audit records (architecture documentation section 14.5).
 *
 * Every entry names actor, action, target, result, time and correlation id.
 * The table is append-only at the database grant level -- the runtime role
 * holds INSERT and SELECT but not UPDATE or DELETE -- so "protected from
 * unauthorized modification" is enforced by Postgres rather than by convention.
 */

export const AUDIT_ACTIONS = [
  'auth.login_succeeded',
  'auth.login_failed',
  'auth.logout',
  'auth.session_revoked',
  'user.created',
  'user.updated',
  'user.disabled',
  'user.roles_changed',
  'user.organization_changed',
  'role.created',
  'role.updated',
  'role.deleted',
  'project.created',
  'project.updated',
  'project.deleted',
  'service.created',
  'service.updated',
  'service.deleted',
  'service_type.created',
  'service_type.updated',
  'service_type.deleted',
  'view.created',
  'view.updated',
  'view.deleted',
  'view.shared',
  'job.retried',
  'integration.created',
  'integration.updated',
  'integration.deleted',
  'integration.paused',
  'integration.resumed',
  'integration.tested',
  'integration.credential_rotated',
  'webhook.rejected',
  'automation.created',
  'automation.updated',
  'automation.deleted',
  'automation.enabled',
  'automation.disabled',
  'automation.executed',
  'annotation.created',
  'export.performed',
  'basemap_settings.updated',
  'map_settings.updated',
  'map_layer.created',
  'map_layer.updated',
  'map_layer.deleted',
  'map_layer.reordered',
  'schedule_calendar.updated',
  'schedule_task.created',
  'schedule_task.updated',
  'schedule_task.deleted',
  'schedule_task.published',
  /* One entry per reporting occasion rather than per row: a weekly update that
     produced thirty audit entries would bury every other kind under itself. */
  'schedule_task.progress_reported',
  'schedule_task.progress_settings_changed',
  'schedule_dependency.created',
  'schedule_dependency.deleted',
  'schedule_session.opened',
  'schedule_session.submitted',
  'schedule_session.applied',
  'schedule_session.cancelled',
  'schedule_session.approval_granted',
  'schedule_session.approval_declined',
  /*
   * Capturing a baseline. Audited because it changes what every variance report
   * in the project is measured against -- and because there is no update and no
   * delete, this row is the only record that a rebaseline happened and who
   * decided it.
   */
  'schedule_baseline.captured',
  /*
   * Access windows. The status transition is the one that matters: confirming
   * a window is what makes the scheduling engine see it at all, and it records
   * an agreement made with a party outside this system entirely -- so the audit
   * row is the only trace of who said the access had been granted.
   */
  'access_window.created',
  'access_window.updated',
  'access_window.status_changed',
  'access_window.deleted',
  'cost_account.created',
  'cost_account.updated',
  'cost_account.moved',
  'cost_account.archived',
  'cost_budget.version_created',
  'cost_budget.version_deleted',
  'cost_budget.approved',
  'cost_budget.line_created',
  'cost_budget.line_updated',
  'cost_budget.line_deleted',
  'cost_budget.line_task_linked',
  'cost_entry.created',
  'cost_entry.updated',
  'cost_entry.voided',
  'cost_entry.imported',
  'meeting.created',
  'meeting.updated',
  'meeting.state_changed',
  'meeting.deleted',
  /*
   * Comments are audited and votes are not. A comment is authored text that one
   * person can remove from another, which is an act worth being able to
   * investigate; a tally is a preference, and auditing preferences turns the
   * access log into an opinion log.
   */
  'comment.created',
  'comment.updated',
  'comment.deleted',
  /*
   * Uploaded, not claimed and not swept. Claiming rides on the owning save's
   * own audit row (`register_item.updated`, `comment.created`, ...), and a
   * second event for the same act would be noise; the orphan sweep is
   * routine maintenance, audited no more than `infrastructure.prune_samples`
   * is.
   */
  'asset.uploaded',
  'register_item.created',
  'register_item.updated',
  'register_item.deleted',
  /*
   * How the project divides its own work. Audited because a division is
   * vocabulary the whole deployment then reads: renaming a stage changes what
   * every chart, filter and report is labelled, and deleting one unfiles every
   * task that named it. Reordering gets its own action rather than one
   * `updated` per row, because that is what happened -- one drag, not eight
   * edits.
   */
  /*
   * Reference lines. Audited because a line is the frame every chainage in the
   * project is quoted against: renaming one relabels every position on it, and
   * deleting one removes every position with it.
   */
  'reference_line.created',
  'reference_line.updated',
  'reference_line.deleted',
  'project_division.created',
  'project_division.updated',
  'project_division.deleted',
  'project_division.reordered',
  'project_division.template_applied',
  'cost_forecast.version_created',
  'cost_forecast.version_updated',
  'cost_forecast.version_deleted',
  'cost_forecast.lines_replaced',
  'cost_forecast.version_issued',
  /*
   * Source and claim changes are audited as heavily as the money itself,
   * because a claim decides who may write which numbers: "the ERP took over
   * these accounts on Tuesday" is the answer to why an entry somebody
   * remembers making is no longer editable.
   */
  'document.created',
  'document.updated',
  'document.deleted',
  'document_revision.created',
  'document_revision.updated',
  'document_revision.issued',
  'document_revision.deleted',
  'schedule.imported',
  'map.imported',
  'party.created',
  'party.updated',
  'party.deleted',
  'contract.created',
  'contract.updated',
  'contract.deleted',
  'cost_source.created',
  'cost_source.updated',
  'cost_source.deleted',
  'cost_source.claim_added',
  'cost_source.claim_removed',
  'cost_source.sync_requested',
  'cost_settings.updated',
  /*
   * Provider actions. Recorded in the tenant's own audit log rather than a
   * separate provider log: the client is entitled to see that their modules
   * were changed or their deployment suspended, and an action visible only to
   * the party who took it is not an audit trail.
   */
  'deployment.identity_updated',
  'deployment.branding_updated',
  'deployment.modules_changed',
  'deployment.stage_changed',
  /*
   * Access control: who may sign in or auto-register at all. Also recorded in
   * the tenant's own audit log, for the same reason the deployment actions
   * above are -- an administrator reviewing "why did this account exist" is
   * entitled to find the domain mapping that created it.
   */
  'access_control.email_domain_created',
  'access_control.email_domain_deleted',
  'access_control.tenant_id_created',
  'access_control.tenant_id_deleted',
  /*
   * Granting or revoking the platform role. The single most sensitive action
   * in the system, audited like any other role change -- see
   * `user.roles_changed` -- rather than differently, because there is nothing
   * about it that isn't already that.
   */
  'platform_admin.granted',
  'platform_admin.revoked',
  /*
   * The fleet. `fleet_assignment.declared` and `.withdrawn` are the two that
   * matter most: a declaration is what an authority reports to a regulator
   * under its own name, so "who said this lorry served that contract, and
   * when" has to survive the row being corrected afterwards.
   */
  'vehicle.created',
  'vehicle.updated',
  'vehicle.deleted',
  'vehicle.attested',
  'framework_agreement.created',
  'framework_agreement.updated',
  'framework_agreement.deleted',
  'fleet_assignment.declared',
  'fleet_assignment.updated',
  'fleet_assignment.withdrawn',
  'fleet_assignment.verified',
  'fleet_assignment.disputed',
  'procurement_unit.created',
  'procurement_unit.updated',
  'procurement_unit.deleted',
  'fleet_settings.updated',
  'fleet_report.exported',
] as const;
export const auditActionSchema = z.enum(AUDIT_ACTIONS);
export type AuditAction = z.infer<typeof auditActionSchema>;

export const auditResultSchema = z.enum(['success', 'failure', 'denied']);
export type AuditResult = z.infer<typeof auditResultSchema>;

export const auditEventSchema = z.object({
  id: z.string().uuid(),
  action: auditActionSchema,
  result: auditResultSchema,
  actorUserId: z.string().uuid().nullable(),
  actorLabel: z.string(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  correlationId: z.string().nullable(),
  /** Redacted at write time; must never contain tokens or credentials. */
  metadata: z.record(z.string(), z.unknown()),
  occurredAt: timestampSchema,
});
export type AuditEvent = z.infer<typeof auditEventSchema>;

export const auditPageSchema = pageSchema(auditEventSchema);

export const auditListQuerySchema = z.object({
  action: auditActionSchema.optional(),
  actorUserId: z.string().uuid().optional(),
  targetType: z.string().max(64).optional(),
  targetId: z.string().max(64).optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type AuditListQuery = z.infer<typeof auditListQuerySchema>;
